import axios from 'axios'
import { adminApiClient } from '@/services/admin/baseService'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import type {
  ApiResponse,
  TronFeeTreasuryOperationType,
  TronOperationClientResourceLocator,
  TronOperationEnvelope,
  TronOperationList,
  TronOperationMode,
  TronOperationState,
  TronPendingOperationItem,
} from '@/types'

const STORAGE_PREFIX = 'cms:admin:tron-pending:v1'
const MAX_PENDING_BYTES = 512 * 1024
const MAX_REQUEST_BODY_BYTES = 64 * 1024
const MAX_HISTORY_SCAN_PAGES = 100
const HISTORY_SCAN_PAGE_SIZE = 100
const API_PREFIX = '/api/v1'
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u
const OPERATION_ID_PATTERN = /^tro_[0-9a-f]{64}$/u
const TX_ID_PATTERN = /^[0-9a-f]{64}$/u
const DECIMAL_PATTERN = /^(?:0|[1-9][0-9]*)$/u
const POSITIVE_DECIMAL_PATTERN = /^[1-9][0-9]*$/u
const OPERATION_STATES = new Set<TronOperationState>([
  'reserved',
  'queued',
  'executing',
  'prepared',
  'broadcast',
  'unknown',
  'completed',
  'rejected',
  'failed',
])
const TERMINAL_STATES = new Set<TronOperationState>([
  'completed',
  'rejected',
  'failed',
])
const TRANSACTION_STATES = new Set<TronOperationState>([
  'prepared',
  'broadcast',
  'unknown',
  'completed',
  'failed',
])
const PUBLIC_ERROR_MESSAGES = {
  TRON_OPERATION_REJECTED: 'Operation was rejected before signing',
  TRON_OPERATION_FAILED: 'Transaction reverted',
  TRON_OPERATION_RECOVERY_REQUIRED: 'Operation requires recovery',
  TRON_OPERATION_PROJECTION_CONFLICT:
    'Operation projection conflicts with stored data',
} as const
const PRE_RESERVATION_ERROR_STATUSES = new Map<string, number>([
  ['IDEMPOTENCY_KEY_REQUIRED', 400],
  ['IDEMPOTENCY_KEY_INVALID', 400],
  ['IDEMPOTENCY_KEY_REUSED', 409],
  ['TRON_OPERATION_REQUEST_INVALID', 400],
  ['TRON_OPERATION_RESOURCE_ID_REQUIRED', 400],
  ['TRON_OPERATION_RESOURCE_ID_INVALID', 400],
  ['TRON_OPERATION_SCOPE_MISMATCH', 409],
  ['TRON_OPERATION_IDENTITY_COLLISION', 409],
  ['TRON_OPERATION_RESOURCE_FROZEN', 409],
  ['TRON_FEE_TREASURY_NOT_READY', 409],
])

type PendingOperationDraft = Pick<
  TronPendingOperationItem,
  'operationType' | 'clientResourceLocator' | 'resourceId'
> & { requestBody: unknown }

export interface HydratedPendingTronOperations {
  items: TronPendingOperationItem[]
  quarantinedKeys: string[]
}

export class TronOperationStorageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TronOperationStorageError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const hasExactKeys = (
  value: Record<string, unknown>,
  required: string[],
  optional: string[] = []
): boolean => {
  const allowed = new Set([...required, ...optional])
  return (
    required.every(key => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every(key => allowed.has(key))
  )
}

const requireIsoTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && new Date(value).toISOString() === value

const requireIdentity = (adminId: string): void => {
  if (!/^[A-Za-z0-9_-]{1,128}$/u.test(adminId)) {
    throw new TronOperationStorageError(
      'Admin identity is invalid for Tron operation storage'
    )
  }
}

const requireIdempotencyKey = (value: string): void => {
  if (!UUID_V4_PATTERN.test(value)) {
    throw new TronOperationStorageError(
      'Tron operation idempotency key is invalid'
    )
  }
}

const requireResourceId = (value: string): void => {
  if (
    value.length === 0 ||
    value !== value.trim() ||
    value !== value.normalize('NFC') ||
    [...value].length > 256 ||
    [...value].some(character => {
      const codePoint = character.codePointAt(0)
      return (
        codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f)
      )
    })
  ) {
    throw new TronOperationStorageError('Tron operation resource ID is invalid')
  }
}

const requireLocatorValue = (value: unknown): string => {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value !== value.trim()
  ) {
    throw new TronOperationStorageError(
      'Tron operation resource locator is invalid'
    )
  }
  return value
}

const getStorage = (): Storage => {
  if (typeof window === 'undefined' || !window.localStorage) {
    throw new TronOperationStorageError(
      'Persistent Tron operation storage is unavailable'
    )
  }
  return window.localStorage
}

const getPrefix = (adminId: string, mode: TronOperationMode): string => {
  requireIdentity(adminId)
  return `${STORAGE_PREFIX}:${mode}:${adminId}:`
}

const getStorageKey = (
  adminId: string,
  mode: TronOperationMode,
  idempotencyKey: string
): string => {
  requireIdempotencyKey(idempotencyKey)
  return `${getPrefix(adminId, mode)}${idempotencyKey}`
}

const readPrefixEntries = (
  storage: Storage,
  prefix: string
): Array<{ key: string; value: string }> => {
  const entries: Array<{ key: string; value: string }> = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key?.startsWith(prefix)) {
      continue
    }
    const value = storage.getItem(key)
    if (value !== null) {
      entries.push({ key, value })
    }
  }
  return entries
}

const byteLength = (value: string): number =>
  new TextEncoder().encode(value).byteLength

const writeAndReadback = (
  storage: Storage,
  key: string,
  item: TronPendingOperationItem,
  prefix: string
): void => {
  const serialized = JSON.stringify(item)
  const otherBytes = readPrefixEntries(storage, prefix).reduce(
    (total, entry) =>
      entry.key === key
        ? total
        : total + byteLength(entry.key) + byteLength(entry.value),
    0
  )
  if (
    otherBytes + byteLength(key) + byteLength(serialized) >
    MAX_PENDING_BYTES
  ) {
    throw new TronOperationStorageError(
      'Persistent Tron operation storage is full'
    )
  }
  try {
    storage.setItem(key, serialized)
    if (storage.getItem(key) !== serialized) {
      throw new Error('readback mismatch')
    }
  } catch {
    throw new TronOperationStorageError(
      'Unable to persist the Tron operation key'
    )
  }
}

export const buildTronOperationEndpoint = (
  operationType: TronFeeTreasuryOperationType,
  locator: TronOperationClientResourceLocator
): string => {
  if (!isRecord(locator)) {
    throw new TronOperationStorageError(
      'Tron operation resource locator is invalid'
    )
  }
  const locatorRecord = locator as Record<string, unknown>
  if (operationType === 'tron_fee_treasury_withdraw') {
    if (!hasExactKeys(locatorRecord, ['chainId', 'token'])) {
      throw new TronOperationStorageError(
        'Tron fee withdraw locator is invalid'
      )
    }
    return `/admin/fee-treasury/${encodeURIComponent(
      requireLocatorValue(locatorRecord.chainId)
    )}/tokens/${encodeURIComponent(requireLocatorValue(locatorRecord.token))}/withdraw`
  }
  if (!hasExactKeys(locatorRecord, ['chainId'])) {
    throw new TronOperationStorageError(
      'Tron fee batch withdraw locator is invalid'
    )
  }
  return `/admin/fee-treasury/${encodeURIComponent(
    requireLocatorValue(locatorRecord.chainId)
  )}/batch-withdraw`
}

const requireRequestBody = (
  value: unknown,
  operationType: TronFeeTreasuryOperationType
): Record<string, unknown> => {
  if (!isRecord(value)) {
    throw new TronOperationStorageError(
      'Tron operation request body is invalid'
    )
  }
  let serialized: string | undefined
  try {
    serialized = JSON.stringify(value)
  } catch {
    throw new TronOperationStorageError(
      'Tron operation request body is invalid'
    )
  }
  if (
    typeof serialized !== 'string' ||
    byteLength(serialized) > MAX_REQUEST_BODY_BYTES
  ) {
    throw new TronOperationStorageError(
      'Tron operation request body is too large'
    )
  }
  const commonValid =
    typeof value.recipientAddress === 'string' &&
    value.recipientAddress.length > 0 &&
    typeof value.auditReason === 'string' &&
    value.auditReason.trim().length >= 3
  if (operationType === 'tron_fee_treasury_withdraw') {
    if (
      !hasExactKeys(value, ['recipientAddress', 'amount', 'auditReason']) ||
      !commonValid ||
      typeof value.amount !== 'string' ||
      !POSITIVE_DECIMAL_PATTERN.test(value.amount)
    ) {
      throw new TronOperationStorageError(
        'Tron fee withdraw request body is invalid'
      )
    }
    return value
  }
  if (
    !hasExactKeys(value, ['recipientAddress', 'withdrawals', 'auditReason']) ||
    !commonValid ||
    !Array.isArray(value.withdrawals) ||
    value.withdrawals.length < 1 ||
    value.withdrawals.length > 10
  ) {
    throw new TronOperationStorageError(
      'Tron fee batch request body is invalid'
    )
  }
  const tokens = new Set<string>()
  value.withdrawals.forEach(withdrawal => {
    if (
      !isRecord(withdrawal) ||
      !hasExactKeys(withdrawal, ['token', 'amount']) ||
      typeof withdrawal.token !== 'string' ||
      withdrawal.token.length === 0 ||
      typeof withdrawal.amount !== 'string' ||
      !POSITIVE_DECIMAL_PATTERN.test(withdrawal.amount) ||
      tokens.has(withdrawal.token)
    ) {
      throw new TronOperationStorageError(
        'Tron fee batch request body is invalid'
      )
    }
    tokens.add(withdrawal.token)
  })
  return value
}

const buildPublicStatusUrl = (
  resourceId: string,
  operationId: string
): string =>
  `${API_PREFIX}/admin/tron-operations/fee_treasury/${encodeURIComponent(
    resourceId
  )}/${operationId}`

const parsePendingItem = (
  value: unknown,
  expectedIdempotencyKey: string
): TronPendingOperationItem => {
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      [
        'idempotencyKey',
        'operationType',
        'endpoint',
        'requestBody',
        'clientResourceLocator',
        'resourceType',
        'resourceId',
        'createdAt',
      ],
      ['operationId', 'statusUrl', 'lastAttemptAt']
    )
  ) {
    throw new TronOperationStorageError(
      'Stored Tron operation item has invalid keys'
    )
  }
  requireIdempotencyKey(expectedIdempotencyKey)
  if (value.idempotencyKey !== expectedIdempotencyKey) {
    throw new TronOperationStorageError(
      'Stored Tron operation key does not match its suffix'
    )
  }
  const operationType = value.operationType as TronFeeTreasuryOperationType
  if (
    ![
      'tron_fee_treasury_withdraw',
      'tron_fee_treasury_batch_withdraw',
    ].includes(operationType)
  ) {
    throw new TronOperationStorageError('Stored Tron operation type is invalid')
  }
  if (
    value.resourceType !== 'fee_treasury' ||
    typeof value.resourceId !== 'string'
  ) {
    throw new TronOperationStorageError(
      'Stored Tron operation scope is invalid'
    )
  }
  requireResourceId(value.resourceId)
  const locator =
    value.clientResourceLocator as TronOperationClientResourceLocator
  const endpoint = buildTronOperationEndpoint(operationType, locator)
  if (value.endpoint !== endpoint) {
    throw new TronOperationStorageError(
      'Stored Tron operation endpoint is invalid'
    )
  }
  const requestBody = requireRequestBody(value.requestBody, operationType)
  if (!requireIsoTimestamp(value.createdAt)) {
    throw new TronOperationStorageError(
      'Stored Tron operation creation time is invalid'
    )
  }
  if (
    value.lastAttemptAt !== undefined &&
    !requireIsoTimestamp(value.lastAttemptAt)
  ) {
    throw new TronOperationStorageError(
      'Stored Tron operation attempt time is invalid'
    )
  }
  if (
    value.operationId !== undefined &&
    (typeof value.operationId !== 'string' ||
      !OPERATION_ID_PATTERN.test(value.operationId))
  ) {
    throw new TronOperationStorageError('Stored Tron operation ID is invalid')
  }
  const expectedStatusUrl =
    typeof value.operationId === 'string'
      ? buildPublicStatusUrl(value.resourceId, value.operationId)
      : undefined
  if (
    value.statusUrl !== expectedStatusUrl ||
    (value.statusUrl !== undefined && typeof value.statusUrl !== 'string')
  ) {
    throw new TronOperationStorageError(
      'Stored Tron operation status URL is invalid'
    )
  }
  return {
    idempotencyKey: expectedIdempotencyKey,
    operationType,
    endpoint,
    requestBody,
    clientResourceLocator: locator,
    resourceType: 'fee_treasury',
    resourceId: value.resourceId,
    ...(value.operationId === undefined
      ? {}
      : { operationId: value.operationId }),
    ...(value.statusUrl === undefined
      ? {}
      : { statusUrl: value.statusUrl as string }),
    createdAt: value.createdAt,
    ...(value.lastAttemptAt === undefined
      ? {}
      : { lastAttemptAt: value.lastAttemptAt }),
  }
}

const createUuidV4 = (): string => {
  if (
    typeof crypto === 'undefined' ||
    typeof crypto.randomUUID !== 'function'
  ) {
    throw new TronOperationStorageError(
      'Secure operation key generation is unavailable'
    )
  }
  const value = crypto.randomUUID().toLowerCase()
  requireIdempotencyKey(value)
  return value
}

export const createPendingTronOperation = (
  adminId: string,
  mode: TronOperationMode,
  draft: PendingOperationDraft
): TronPendingOperationItem => {
  const storage = getStorage()
  const idempotencyKey = createUuidV4()
  requireResourceId(draft.resourceId)
  const item: TronPendingOperationItem = {
    idempotencyKey,
    operationType: draft.operationType,
    endpoint: buildTronOperationEndpoint(
      draft.operationType,
      draft.clientResourceLocator
    ),
    requestBody: requireRequestBody(draft.requestBody, draft.operationType),
    clientResourceLocator: draft.clientResourceLocator,
    resourceType: 'fee_treasury',
    resourceId: draft.resourceId,
    createdAt: new Date().toISOString(),
  }
  const prefix = getPrefix(adminId, mode)
  writeAndReadback(
    storage,
    getStorageKey(adminId, mode, idempotencyKey),
    item,
    prefix
  )
  return item
}

export const hydratePendingTronOperations = (
  adminId: string,
  mode: TronOperationMode
): HydratedPendingTronOperations => {
  const storage = getStorage()
  const prefix = getPrefix(adminId, mode)
  const entries = readPrefixEntries(storage, prefix)
  const totalBytes = entries.reduce(
    (total, entry) => total + byteLength(entry.key) + byteLength(entry.value),
    0
  )
  if (totalBytes > MAX_PENDING_BYTES) {
    throw new TronOperationStorageError(
      'Persistent Tron operation storage is over capacity'
    )
  }
  const items: TronPendingOperationItem[] = []
  const quarantinedKeys: string[] = []
  entries.forEach(entry => {
    try {
      items.push(
        parsePendingItem(
          JSON.parse(entry.value),
          entry.key.slice(prefix.length)
        )
      )
    } catch {
      quarantinedKeys.push(entry.key)
    }
  })
  return {
    items: items.sort((left, right) =>
      left.createdAt.localeCompare(right.createdAt)
    ),
    quarantinedKeys,
  }
}

const requireVerificationEvidence = (value: unknown): void => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      'expectedEthChainId',
      'expectedGenesisBlockId',
      'rpcEndpointIdentities',
      'transactionObservedAtMs',
      'solidBlockNumber',
      'resultCode',
    ]) ||
    typeof value.expectedEthChainId !== 'string' ||
    !/^0x[0-9a-f]+$/u.test(value.expectedEthChainId) ||
    typeof value.expectedGenesisBlockId !== 'string' ||
    !TX_ID_PATTERN.test(value.expectedGenesisBlockId) ||
    !Array.isArray(value.rpcEndpointIdentities) ||
    value.rpcEndpointIdentities.length === 0 ||
    value.rpcEndpointIdentities.length > 4 ||
    new Set(value.rpcEndpointIdentities).size !==
      value.rpcEndpointIdentities.length ||
    value.rpcEndpointIdentities.some(
      item =>
        typeof item !== 'string' || item.length === 0 || item.length > 100
    ) ||
    !Number.isSafeInteger(value.transactionObservedAtMs) ||
    (value.transactionObservedAtMs as number) < 0 ||
    typeof value.solidBlockNumber !== 'string' ||
    !DECIMAL_PATTERN.test(value.solidBlockNumber) ||
    typeof value.resultCode !== 'string' ||
    !/^[A-Z][A-Z0-9_]{0,63}$/u.test(value.resultCode)
  ) {
    throw new Error('Tron operation verification evidence is invalid')
  }
}

const requireTerminalResult = (
  value: unknown,
  state: 'completed' | 'failed',
  txId: string
): void => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['outcome', 'txId', 'blockNumber', 'evidence']) ||
    value.outcome !== (state === 'completed' ? 'confirmed' : 'reverted') ||
    value.txId !== txId ||
    typeof value.blockNumber !== 'string' ||
    !DECIMAL_PATTERN.test(value.blockNumber)
  ) {
    throw new Error('Tron operation result is invalid')
  }
  requireVerificationEvidence(value.evidence)
  const evidence = value.evidence as Record<string, unknown>
  if ((state === 'completed') !== (evidence.resultCode === 'SUCCESS')) {
    throw new Error('Tron operation result code is invalid')
  }
}

const requirePublicError = (
  value: unknown,
  state: TronOperationState
): void => {
  if (value === undefined) {
    if (state === 'rejected' || state === 'failed' || state === 'unknown') {
      throw new Error('Tron operation public error is missing')
    }
    return
  }
  if (!isRecord(value) || !hasExactKeys(value, ['code', 'message'])) {
    throw new Error('Tron operation public error is invalid')
  }
  const code = value.code as keyof typeof PUBLIC_ERROR_MESSAGES
  if (
    !(code in PUBLIC_ERROR_MESSAGES) ||
    value.message !== PUBLIC_ERROR_MESSAGES[code]
  ) {
    throw new Error('Tron operation public error is invalid')
  }
  const validForState =
    code === 'TRON_OPERATION_REJECTED'
      ? state === 'rejected'
      : code === 'TRON_OPERATION_FAILED'
        ? state === 'failed'
        : code === 'TRON_OPERATION_RECOVERY_REQUIRED'
          ? state === 'unknown'
          : state === 'unknown'
  if (!validForState) {
    throw new Error('Tron operation public error does not match its state')
  }
}

const parseEnvelope = (
  value: unknown,
  expected: Pick<TronPendingOperationItem, 'operationType' | 'resourceId'>,
  mode: TronOperationMode
): TronOperationEnvelope => {
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      [
        'schemaVersion',
        'operationId',
        'operationType',
        'mode',
        'resourceType',
        'resourceId',
        'sequence',
        'state',
        'statusUrl',
        'createdAt',
        'updatedAt',
      ],
      ['txId', 'result', 'error', 'terminalAt']
    ) ||
    value.schemaVersion !== 'v1'
  ) {
    throw new Error('Tron operation response is invalid')
  }
  const state = value.state as TronOperationState
  if (
    typeof value.operationId !== 'string' ||
    !OPERATION_ID_PATTERN.test(value.operationId) ||
    value.operationType !== expected.operationType ||
    value.mode !== mode ||
    value.resourceType !== 'fee_treasury' ||
    value.resourceId !== expected.resourceId ||
    !OPERATION_STATES.has(state) ||
    !Number.isSafeInteger(value.sequence) ||
    (value.sequence as number) < 1
  ) {
    throw new Error('Tron operation response scope is invalid')
  }
  if (
    value.statusUrl !==
    buildPublicStatusUrl(expected.resourceId, value.operationId)
  ) {
    throw new Error('Tron operation response status URL is invalid')
  }
  if (
    !requireIsoTimestamp(value.createdAt) ||
    !requireIsoTimestamp(value.updatedAt)
  ) {
    throw new Error('Tron operation response timestamp is invalid')
  }
  const terminal = TERMINAL_STATES.has(state)
  if (
    (value.terminalAt !== undefined) !== terminal ||
    (value.terminalAt !== undefined && !requireIsoTimestamp(value.terminalAt))
  ) {
    throw new Error('Tron operation terminal timestamp is invalid')
  }
  const transactionExpected = TRANSACTION_STATES.has(state)
  if (
    (value.txId !== undefined) !== transactionExpected ||
    (value.txId !== undefined &&
      (typeof value.txId !== 'string' || !TX_ID_PATTERN.test(value.txId)))
  ) {
    throw new Error('Tron operation transaction ID is invalid')
  }
  const resultExpected = state === 'completed' || state === 'failed'
  if ((value.result !== undefined) !== resultExpected) {
    throw new Error('Tron operation result is invalid')
  }
  if (resultExpected) {
    requireTerminalResult(value.result, state, value.txId as string)
  }
  requirePublicError(value.error, state)
  return value as unknown as TronOperationEnvelope
}

const responseEnvelope = (
  value: unknown,
  item: Pick<TronPendingOperationItem, 'operationType' | 'resourceId'>,
  mode: TronOperationMode
): TronOperationEnvelope => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['success', 'data']) ||
    value.success !== true ||
    !isRecord(value.data)
  ) {
    throw new Error('Tron operation response is missing its operation')
  }
  return parseEnvelope(value.data, item, mode)
}

const readExactErrorCode = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) {
    return null
  }
  const body = error.response?.data
  if (
    !isRecord(body) ||
    !hasExactKeys(body, ['success', 'error']) ||
    body.success !== false ||
    !isRecord(body.error) ||
    !hasExactKeys(body.error, ['code', 'message']) ||
    typeof body.error.code !== 'string' ||
    typeof body.error.message !== 'string'
  ) {
    return null
  }
  return body.error.code
}

const isExactNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 404 &&
  readExactErrorCode(error) === 'TRON_OPERATION_NOT_FOUND'

const isPreReservationError = (error: unknown): boolean => {
  const code = readExactErrorCode(error)
  return (
    code !== null &&
    axios.isAxiosError(error) &&
    error.response?.status === PRE_RESERVATION_ERROR_STATUSES.get(code)
  )
}

const terminalErrorEnvelope = (
  error: unknown,
  item: TronPendingOperationItem,
  mode: TronOperationMode
): TronOperationEnvelope | null => {
  if (
    !axios.isAxiosError(error) ||
    ![409, 422].includes(error.response?.status ?? 0)
  ) {
    return null
  }
  try {
    const body = error.response?.data
    if (
      !isRecord(body) ||
      !hasExactKeys(body, ['success', 'error', 'data']) ||
      body.success !== false ||
      !isRecord(body.error) ||
      !hasExactKeys(body.error, ['code', 'message']) ||
      !isRecord(body.data)
    ) {
      return null
    }
    const envelope = parseEnvelope(body.data, item, mode)
    const expectedState = error.response?.status === 409 ? 'rejected' : 'failed'
    if (
      envelope.state !== expectedState ||
      !envelope.error ||
      body.error.code !== envelope.error.code ||
      body.error.message !== envelope.error.message
    ) {
      return null
    }
    return envelope
  } catch {
    return null
  }
}

const storeEnvelope = (
  adminId: string,
  mode: TronOperationMode,
  item: TronPendingOperationItem,
  envelope: TronOperationEnvelope
): void => {
  const storage = getStorage()
  const key = getStorageKey(adminId, mode, item.idempotencyKey)
  if (TERMINAL_STATES.has(envelope.state)) {
    storage.removeItem(key)
    return
  }
  writeAndReadback(
    storage,
    key,
    {
      ...item,
      operationId: envelope.operationId,
      statusUrl: envelope.statusUrl,
    },
    getPrefix(adminId, mode)
  )
}

export const submitPendingTronOperation = async (
  adminId: string,
  mode: TronOperationMode,
  input: TronPendingOperationItem
): Promise<TronOperationEnvelope> => {
  const storage = getStorage()
  const item = parsePendingItem(input, input.idempotencyKey)
  const attempted: TronPendingOperationItem = {
    ...item,
    lastAttemptAt: new Date().toISOString(),
  }
  const key = getStorageKey(adminId, mode, item.idempotencyKey)
  writeAndReadback(storage, key, attempted, getPrefix(adminId, mode))
  const modeConfig = buildModeAwareConfig(mode)
  try {
    const response = await adminApiClient.post<
      ApiResponse<TronOperationEnvelope>
    >(attempted.endpoint, attempted.requestBody, {
      ...modeConfig,
      headers: {
        ...(modeConfig?.headers ?? {}),
        'Idempotency-Key': attempted.idempotencyKey,
        'X-TRON-Resource-Id': attempted.resourceId,
      },
    })
    const envelope = responseEnvelope(response.data, attempted, mode)
    if (response.status !== (envelope.state === 'completed' ? 200 : 202)) {
      throw new Error('Tron operation HTTP status does not match its state')
    }
    storeEnvelope(adminId, mode, attempted, envelope)
    return envelope
  } catch (error) {
    const terminalEnvelope = terminalErrorEnvelope(error, attempted, mode)
    if (terminalEnvelope) {
      storeEnvelope(adminId, mode, attempted, terminalEnvelope)
      return terminalEnvelope
    }
    if (isPreReservationError(error)) {
      storage.removeItem(key)
    }
    throw error
  }
}

const statusRequestPath = (statusUrl: string): string => {
  if (!statusUrl.startsWith(`${API_PREFIX}/admin/tron-operations/`)) {
    throw new TronOperationStorageError(
      'Stored Tron operation status URL is invalid'
    )
  }
  return statusUrl.slice(API_PREFIX.length)
}

export const getTronOperationStatus = async (
  input: TronPendingOperationItem,
  mode: TronOperationMode
): Promise<TronOperationEnvelope> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  if (!item.statusUrl || !item.operationId) {
    throw new Error('Tron operation status is not available before reservation')
  }
  const response = await adminApiClient.get<ApiResponse<TronOperationEnvelope>>(
    statusRequestPath(item.statusUrl),
    buildModeAwareConfig(mode)
  )
  return responseEnvelope(response.data, item, mode)
}

export const lookupTronOperation = async (
  input: TronPendingOperationItem,
  mode: TronOperationMode
): Promise<TronOperationEnvelope> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  const modeConfig = buildModeAwareConfig(mode)
  const response = await adminApiClient.get<ApiResponse<TronOperationEnvelope>>(
    `/admin/tron-operations/fee_treasury/${encodeURIComponent(
      item.resourceId
    )}/by-idempotency-key`,
    {
      ...modeConfig,
      headers: {
        ...(modeConfig?.headers ?? {}),
        'Idempotency-Key': item.idempotencyKey,
      },
    }
  )
  return responseEnvelope(response.data, item, mode)
}

export const reconcilePendingTronOperation = async (
  adminId: string,
  mode: TronOperationMode,
  input: TronPendingOperationItem
): Promise<TronOperationEnvelope> => {
  const item = parsePendingItem(input, input.idempotencyKey)
  let envelope: TronOperationEnvelope
  if (item.statusUrl) {
    envelope = await getTronOperationStatus(item, mode)
  } else {
    try {
      envelope = await lookupTronOperation(item, mode)
    } catch (error) {
      if (!isExactNotFound(error)) {
        throw error
      }
      if (!item.lastAttemptAt) {
        throw new Error('Unsent Tron operation draft has no server operation')
      }
      return submitPendingTronOperation(adminId, mode, item)
    }
  }
  storeEnvelope(adminId, mode, item, envelope)
  return envelope
}

export const listTronOperations = async (
  resourceId: string,
  mode: TronOperationMode,
  afterSequence = 0,
  limit = 50
): Promise<TronOperationList> => {
  requireResourceId(resourceId)
  if (!Number.isSafeInteger(afterSequence) || afterSequence < 0) {
    throw new Error('Tron operation sequence is invalid')
  }
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
    throw new Error('Tron operation list limit is invalid')
  }
  const response = await adminApiClient.get<ApiResponse<TronOperationList>>(
    `/admin/tron-operations/fee_treasury/${encodeURIComponent(resourceId)}`,
    {
      ...buildModeAwareConfig(mode),
      params: { afterSequence, limit },
    }
  )
  const body = response.data
  const data = body?.data
  if (
    !isRecord(body) ||
    !hasExactKeys(body, ['success', 'data']) ||
    body.success !== true ||
    !isRecord(data) ||
    !hasExactKeys(data, [
      'schemaVersion',
      'mode',
      'resourceType',
      'resourceId',
      'items',
      'nextAfterSequence',
    ]) ||
    data.schemaVersion !== 'v1' ||
    data.mode !== mode ||
    data.resourceType !== 'fee_treasury' ||
    data.resourceId !== resourceId ||
    !Array.isArray(data.items)
  ) {
    throw new Error('Tron operation list response is invalid')
  }
  const items = data.items.map(item => {
    if (
      !isRecord(item) ||
      ![
        'tron_fee_treasury_withdraw',
        'tron_fee_treasury_batch_withdraw',
      ].includes(item.operationType as string)
    ) {
      throw new Error('Tron operation list item is invalid')
    }
    return parseEnvelope(
      item,
      {
        operationType: item.operationType as TronFeeTreasuryOperationType,
        resourceId,
      },
      mode
    )
  })
  for (let index = 0; index < items.length; index += 1) {
    const previous = index === 0 ? afterSequence : items[index - 1].sequence
    if (items[index].sequence <= previous) {
      throw new Error('Tron operation list sequence is invalid')
    }
  }
  const expectedCursor = items.at(-1)?.sequence ?? null
  if (data.nextAfterSequence !== expectedCursor) {
    throw new Error('Tron operation list cursor is invalid')
  }
  return {
    schemaVersion: 'v1',
    mode,
    resourceType: 'fee_treasury',
    resourceId,
    items,
    nextAfterSequence: expectedCursor,
  }
}

export const getLatestTronOperation = async (
  resourceId: string,
  mode: TronOperationMode
): Promise<TronOperationEnvelope | null> => {
  let afterSequence = 0
  let latest: TronOperationEnvelope | null = null
  for (let page = 0; page < MAX_HISTORY_SCAN_PAGES; page += 1) {
    const result = await listTronOperations(
      resourceId,
      mode,
      afterSequence,
      HISTORY_SCAN_PAGE_SIZE
    )
    if (result.items.length === 0) {
      return latest
    }
    latest = result.items.at(-1) ?? latest
    if (result.items.length < HISTORY_SCAN_PAGE_SIZE) {
      return latest
    }
    if (result.nextAfterSequence === null) {
      throw new Error('Tron operation history cursor is unavailable')
    }
    afterSequence = result.nextAfterSequence
  }
  throw new Error('Tron operation history exceeds the safe recovery scan limit')
}

export const tronOperationService = {
  createPending: createPendingTronOperation,
  hydratePending: hydratePendingTronOperations,
  submitPending: submitPendingTronOperation,
  getStatus: getTronOperationStatus,
  lookup: lookupTronOperation,
  reconcilePending: reconcilePendingTronOperation,
  list: listTronOperations,
  latest: getLatestTronOperation,
}
